import { useState, useEffect, useRef, useCallback } from 'react';
import Header from './components/Header/Header';
import DiagramEditor from './components/DiagramEditor/DiagramEditor';
import DiagramRenderer from './components/DiagramRenderer/DiagramRenderer';
import SavedDiagrams from './components/SavedDiagrams/SavedDiagrams';
import AiChat from './components/DiagramEditor/AiChat';
import ApiKeyModal from './components/ApiKeyModal/ApiKeyModal';
import PersonaModal from './components/Settings/PersonaModal';
import { DiagramProvider, useDiagram } from './contexts/DiagramContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { PersonaProvider } from './contexts/PersonaContext';
import { ApiKeyProvider, useApiKey } from './contexts/ApiKeyContext';
import useApiKeyModal from './hooks/useApiKeyModal';
import { setApiKeyClearFunction, setApiKeyGetFunction } from './services/diagramApi';
import './styles/App.css';

function AppContent() {
  const [showAiChat, setShowAiChat] = useState(true); // Always show AI chat
  const [aiChatWidth, setAiChatWidth] = useState(25); // 25% by default
  const [editorWidth, setEditorWidth] = useState(40); // 40% by default
  const [selectedCode, setSelectedCode] = useState('');
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const { code, updateCode } = useDiagram();
  const editorRef = useRef(null);
  
  // API key modal management
  const { 
    isModalOpen, 
    openModal,
    closeModal, 
    handleSuccess, 
    handleSkip,
    hasApiKey 
  } = useApiKeyModal();
  
  // Get API key context to access clearApiKey function and getApiKey function
  const { clearApiKey, getApiKey } = useApiKey();
  
  // Set the API key clear function for the diagram API service
  useEffect(() => {
    setApiKeyClearFunction(clearApiKey);
    setApiKeyGetFunction(getApiKey);
  }, [clearApiKey, getApiKey]);
  
  // Function to open the API key modal
  const openApiKeyModal = useCallback(() => {
    openModal();
  }, [openModal]);

  // Function to toggle persona modal
  const togglePersonaModal = useCallback(() => {
    setShowPersonaModal(prev => !prev);
  }, []);
  
  // Check if AI features are enabled based on environment variables and API key
  const aiEnabled = (import.meta.env.VITE_ENABLE_AI_FEATURES === 'true' || true) && hasApiKey;

  // Set selected code when code changes
  useEffect(() => {
    if (code) {
      setSelectedCode(code);
    }
  }, [code]);

  // Handle code updates from AI Chat
  const handleCodeUpdate = (newCode) => {
    if (newCode) {
      updateCode(newCode);
    }
  };

  return (
    <div className="app-container">
      {/* API Key Modal */}
      <ApiKeyModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={handleSuccess}
        onSkip={handleSkip}
      />
      
      {/* Persona Modal */}
      <PersonaModal 
        isOpen={showPersonaModal} 
        onClose={() => setShowPersonaModal(false)} 
      />
      
      <Header 
        aiEnabled={aiEnabled}
        showAiChat={showAiChat}
        setShowAiChat={setShowAiChat}
        setSelectedCode={() => setSelectedCode(code)}
        openApiKeyModal={openApiKeyModal}
        hasApiKey={hasApiKey}
        togglePersonaModal={togglePersonaModal}
      />
      <main className="main-content three-column-layout">
        {/* AI Chat Column */}
        <div className="column chat-column" style={{ width: `${aiChatWidth}%` }}>
          <AiChat 
            isOpen={true} 
            onClose={() => setShowAiChat(false)}
            initialQuery={selectedCode}
            onCodeUpdate={handleCodeUpdate}
            apiKeyRequired={!hasApiKey}
            onCustomizePersona={togglePersonaModal}
          />
        </div>
        
        {/* Resizer between Chat and Editor */}
        <div 
          className="resizer-handle-vertical"
          onMouseDown={(e) => {
            const handleMouseMove = (moveEvent) => {
              const container = document.querySelector('.main-content');
              const containerRect = container.getBoundingClientRect();
              const percentage = ((moveEvent.clientX - containerRect.left) / containerRect.width) * 100;
              const newWidth = Math.min(Math.max(percentage, 15), 40);
              setAiChatWidth(newWidth);
              
              // Adjust editor width to maintain proportions
              const remainingSpace = 100 - newWidth;
              const editorRatio = editorWidth / (editorWidth + (100 - aiChatWidth - editorWidth));
              setEditorWidth(remainingSpace * editorRatio);
            };
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <div className="resizer-handle-icon-vertical"></div>
        </div>
        
        {/* Editor Column */}
        <div className="column editor-column" style={{ width: `${editorWidth}%` }} ref={editorRef}>
          <DiagramEditor />
        </div>
        
        {/* Resizer between Editor and Preview */}
        <div 
          className="resizer-handle-vertical"
          onMouseDown={(e) => {
            const handleMouseMove = (moveEvent) => {
              const container = document.querySelector('.main-content');
              const containerRect = container.getBoundingClientRect();
              const totalWidth = containerRect.width;
              const chatWidth = aiChatWidth * totalWidth / 100;
              const percentage = ((moveEvent.clientX - containerRect.left - chatWidth) / (totalWidth - chatWidth)) * (100 - aiChatWidth);
              const newWidth = Math.min(Math.max(percentage, 20), 60);
              setEditorWidth(newWidth);
            };
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <div className="resizer-handle-icon-vertical"></div>
        </div>
        
        {/* Preview Column */}
        <div className="column preview-column" style={{ width: `${100 - aiChatWidth - editorWidth}%` }}>
          <DiagramRenderer />
        </div>
        
        {/* Hidden Saved Diagrams Container */}
        <div className="saved-diagrams-container">
          <SavedDiagrams />
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <ApiKeyProvider>
      <SettingsProvider>
        <PersonaProvider>
          <DiagramProvider>
            <AppContent />
          </DiagramProvider>
        </PersonaProvider>
      </SettingsProvider>
    </ApiKeyProvider>
  );
}

export default App; 